'use client';

import { useTranslations } from 'next-intl';
import { Gleam, IconButton } from '@zillow/constellation-10';
import { SlidersHorizontal } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';

type AllFiltersButtonProps = {
  size: 'sm' | 'lg';
  activeCount: number;
  onClick: () => void;
};

/**
 * Trigger for the AllFiltersModal — Constellation `IconButton` wrapped in
 * a `Gleam` notification badge when filters are active.
 *
 * The SlidersHorizontal icon is always rendered. Gleam wraps the entire
 * IconButton (not the inner icon slot) so its absolute-positioned badge
 * lands at the top corner of the button instead of competing with the
 * icon's flex layout. The badge uses Gleam's `info` tone for the aqua
 * notification look (the default tone is alert-red).
 *
 * The IconButton's `title` doubles as the screen-reader label and includes
 * the active count when filters are applied.
 */
export default function AllFiltersButton({ size, activeCount, onClick }: AllFiltersButtonProps) {
  const tFilters = useTranslations('filters');
  const showCount = activeCount > 0;

  const accessibleLabel = showCount ? `${tFilters('allFilters')}, ${activeCount} active` : tFilters('allFilters');

  const button = (
    <IconButton
      title={accessibleLabel}
      shape="circle"
      size={size}
      tone="neutral"
      emphasis="bare"
      onClick={onClick}
      icon={<LucideIcon icon={SlidersHorizontal} />}
    />
  );

  if (!showCount) return button;

  return (
    <Gleam count={activeCount} tone="info">
      {button}
    </Gleam>
  );
}
